// src/features/billing/AccountBalanceCard.tsx
// Current or historical balance for a Chart-of-Accounts account (GetAccountBalance).
// Embedded in BillingDetailPage (the chart-of-accounts account detail page).
//
// The generated store method (BillingStore.GetAccountBalance) only surfaces
// the `account` field of GetAccountBalanceResponse and drops `balance`/`as_of`
// — a codegen gap. This calls the endpoint directly to get the real balance,
// same approach as other IAs' documented call-site fixes for generator drift.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { billingApi } from '@core/api';
import { Button, Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { formatMoney, formatDate, errMessage } from './format';

export interface AccountBalanceCardProps {
  accountId: string;
}

export function AccountBalanceCard({ accountId }: AccountBalanceCardProps) {
  const { t } = useI18n();
  const [asOf, setAsOf] = useState('');
  const [result, setResult] = useState<{ balance?: any; as_of?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingApi.GetAccountBalance(
        accountId,
        asOf ? { params: { as_of: new Date(asOf).toISOString() } } : undefined,
      );
      setResult({ balance: response.balance, as_of: response.as_of as unknown as string });
    } catch (err) {
      setError(errMessage(err, t('billing.accountBalance.loadError')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.accountBalance.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? <ErrorAlert error={error} /> : null}
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1" htmlFor="account-balance-as-of">
              {t('billing.accountBalance.asOf')}
            </label>
            <input
              id="account-balance-as-of"
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
              className="rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground"
            />
          </div>
          <Button size="sm" variant="secondary" onClick={fetchBalance} isLoading={loading}>
            {t('billing.accountBalance.refresh')}
          </Button>
        </div>
        {result ? (
          <div className="pt-2 border-t border-border">
            <p className="text-2xl font-bold text-foreground">{formatMoney(result.balance)}</p>
            <p className="text-xs text-muted-foreground">
              {t('billing.accountBalance.asOfLabel')}: {result.as_of ? formatDate(result.as_of) : t('billing.accountBalance.now')}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('billing.accountBalance.empty')}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default AccountBalanceCard;
